import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import Modal from "../../design-system/components/modals/Modal";
import { PrimaryButton, SecondaryButton } from "../../design-system/components/buttons";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import { apiFetch } from "../../core/utils/api";

/**
 * Self-service "Update payment method" modal.
 *
 * Uses Stripe Elements in **setup mode** (SetupIntent) to collect a new card
 * without charging the customer. After Stripe confirms the SetupIntent we
 * post the resulting `payment_method` id to our backend, which:
 *   - sets it as the default on the customer + every active subscription,
 *   - retries any past-due invoices, and
 *   - mirrors the row locally + emits an audit event.
 *
 * The component is self-contained: it owns its loading / error state and
 * notifies the parent via `onUpdated()` when everything succeeded so the
 * page can refetch the new payment-method details.
 */

const API_ENDPOINTS = {
  STRIPE_CONFIG: "/api/stripe/config/",
  PM_SETUP_INTENT: "/api/stripe/payment-method/setup-intent/",
  PM_SET_DEFAULT: "/api/stripe/payment-method/set-default/",
};

const getStripePublishableKeyFromEnv = () =>
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
  process.env.REACT_APP_STRIPE_PUBLISHABLE ||
  "";

const PaymentMethodForm = ({ onSubmit, onCancel, submitting, disabled }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [innerSubmitting, setInnerSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!stripe || !elements) return;
    setInnerSubmitting(true);
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (result?.error) {
        await onSubmit({ error: result.error.message || "Could not save your card." });
        return;
      }
      const setupIntent = result?.setupIntent;
      const status = setupIntent?.status || "";
      if (status === "succeeded") {
        const pmId =
          (typeof setupIntent.payment_method === "string" ? setupIntent.payment_method : null) ||
          setupIntent?.payment_method?.id ||
          null;
        if (!pmId) {
          await onSubmit({ error: "Stripe did not return a payment method id." });
          return;
        }
        await onSubmit({ paymentMethodId: pmId });
        return;
      }
      if (status === "requires_action") {
        await onSubmit({ error: "Additional authentication is required. Please try again." });
        return;
      }
      await onSubmit({ error: "Card could not be saved. Please try again." });
    } catch (e) {
      await onSubmit({ error: e?.message || "Card could not be saved. Please try again." });
    } finally {
      setInnerSubmitting(false);
    }
  };

  const isWorking = submitting || innerSubmitting;

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border ${borders.default} ${surfaces.primary} p-4`}>
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      <div className="flex items-center justify-end gap-3">
        <SecondaryButton type="button" size="sm" onClick={onCancel} disabled={isWorking}>
          Cancel
        </SecondaryButton>
        <PrimaryButton
          size="sm"
          onClick={handleConfirm}
          loading={isWorking}
          disabled={disabled || !stripe || !elements || isWorking}
        >
          Save card
        </PrimaryButton>
      </div>
    </div>
  );
};

const UpdatePaymentMethodModal = ({ isOpen, onClose, onUpdated, isPastDueRecovery = false }) => {
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [setupError, setSetupError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [publishableKey, setPublishableKey] = useState(() => getStripePublishableKeyFromEnv());

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  const isDarkMode =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const stripeAppearance = useMemo(
    () => ({
      theme: isDarkMode ? "night" : "stripe",
      variables: {
        colorPrimary: isDarkMode ? "#60a5fa" : "#2563eb",
        colorBackground: isDarkMode ? "#121212" : "#ffffff",
        colorText: isDarkMode ? "#d4d4d4" : "#111827",
      },
    }),
    [isDarkMode]
  );

  useEffect(() => {
    if (!isOpen) return;
    setSetupClientSecret(null);
    setSetupError(null);
    setSubmitError(null);
    setSubmitting(false);
    setLoadingIntent(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (publishableKey) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch(API_ENDPOINTS.STRIPE_CONFIG, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await resp.json().catch(() => ({}));
        const key = String(data?.publishable_key || "").trim();
        if (!cancelled && resp.ok && key) setPublishableKey(key);
      } catch {
        // Surfaced through the no-Stripe-key state below.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, publishableKey]);

  useEffect(() => {
    if (!isOpen) return;
    if (setupClientSecret) return;
    let cancelled = false;
    (async () => {
      setLoadingIntent(true);
      setSetupError(null);
      try {
        const resp = await apiFetch(API_ENDPOINTS.PM_SETUP_INTENT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data?.error || "Could not start the update flow.");
        }
        const secret = String(data?.setup_intent_client_secret || "").trim();
        if (!secret) throw new Error("Stripe did not return a client secret.");
        if (!cancelled) setSetupClientSecret(secret);
      } catch (e) {
        if (!cancelled) setSetupError(e?.message || "Could not start the update flow.");
      } finally {
        if (!cancelled) setLoadingIntent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, setupClientSecret]);

  const handleSubmit = async ({ paymentMethodId, error }) => {
    if (error) {
      setSubmitError(error);
      return;
    }
    if (!paymentMethodId) {
      setSubmitError("Stripe did not return a payment method id.");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await apiFetch(API_ENDPOINTS.PM_SET_DEFAULT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stripe_payment_method_id: paymentMethodId }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data?.error || "Could not save your new payment method.");
      }

      onUpdated?.({
        defaultPaymentMethod: data?.default_payment_method || null,
        retriedPastDue: !!data?.retried_past_due,
        retryFailed: !!data?.retry_failed,
      });
      onClose?.();
    } catch (e) {
      setSubmitError(e?.message || "Could not save your new payment method.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!submitting) onClose?.();
      }}
      title={isPastDueRecovery ? "Update card to retry payment" : "Update payment method"}
      size="md"
      closeOnOverlayClick={!submitting}
      closeOnEsc={!submitting}
      showCloseButton={!submitting}
    >
      <div className="space-y-4">
        <p className={`${typography.bodySmall} ${textColors.secondary}`}>
          {isPastDueRecovery
            ? "We'll retry your most recent invoice with the new card right after you save it."
            : "Future renewals will use this card. Your existing subscription stays active."}
        </p>

        {setupError ? (
          <div
            role="alert"
            className={`rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2`}
          >
            <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{setupError}</p>
          </div>
        ) : null}

        {submitError ? (
          <div
            role="alert"
            className={`rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2`}
          >
            <p className={`${typography.bodySmall} text-red-700 dark:text-red-300`}>{submitError}</p>
          </div>
        ) : null}

        {!stripePromise ? (
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            Loading payment form…
          </div>
        ) : loadingIntent || !setupClientSecret ? (
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            Preparing secure payment form…
          </div>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret: setupClientSecret, appearance: stripeAppearance }}
          >
            <PaymentMethodForm
              onSubmit={handleSubmit}
              onCancel={() => {
                if (!submitting) onClose?.();
              }}
              submitting={submitting}
              disabled={false}
            />
          </Elements>
        )}
      </div>
    </Modal>
  );
};

export default UpdatePaymentMethodModal;
